import {
  COUNTRY_OPTIONS,
  DEPARTMENTS,
  DOCUMENT_FIELDS,
  EMPLOYMENT_TYPES,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PAY_FREQUENCIES,
  ROLE_OPTIONS,
  STATE_OPTIONS,
} from "../assets/instateamMasterConfig";
import {
  DateInput,
  DocumentUploadCard,
  LabeledInput,
  PhoneInput,
  ProfileUploadCard,
  SelectInput,
  StaticInput,
} from "../components/InstateamMasterComponents";

const TAX_REGIME_OPTIONS = ["Old Regime", "New Regime"];

export default function AddEmployeePage({
  employeeForm,
  employeeFiles,
  onFieldChange,
  onFileChange,
  onCancel,
  onSubmit,
  isSavingEmployee,
  mode = "create",
  employeeIdValue = "Auto generated",
  showPasswordFields = true,
  existingProfilePhoto = null,
  existingDocuments = {},
  submitLabel = "",
  departmentOptions = DEPARTMENTS,
  designationOptions = [],
}) {
  const reportingManagerOptions = Array.from(
    new Set(
      [
        "Admin User",
        "Emma Watson",
        "John Doe",
        "Michael Clark",
        "Olivia Taylor",
        employeeForm.reportingManager,
      ].filter(Boolean)
    )
  );

  return (
    <form className="employee-form-page" onSubmit={onSubmit}>
      <section className="form-section-card">
        <div className="section-heading">
          <h3>Personal Information</h3>
        </div>

        <div className="employee-form-grid personal">
          <LabeledInput
            label="Full Name"
            required
            value={employeeForm.fullName}
            onChange={(value) => onFieldChange((current) => ({ ...current, fullName: value }))}
            placeholder="Enter full name"
          />

          <LabeledInput
            label="Email Address"
            required
            value={employeeForm.email}
            onChange={(value) => onFieldChange((current) => ({ ...current, email: value }))}
            placeholder="Enter email address"
          />

          <ProfileUploadCard
            file={employeeFiles.profilePhoto}
            existingFile={existingProfilePhoto}
            onSelect={(file) => onFileChange((current) => ({ ...current, profilePhoto: file }))}
          />

          <PhoneInput
            value={employeeForm.phoneNumber}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, phoneNumber: value }))
            }
          />

          <DateInput
            label="Date of Birth"
            required
            value={employeeForm.dateOfBirth}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, dateOfBirth: value }))
            }
          />

          <SelectInput
            label="Gender"
            required
            value={employeeForm.gender}
            onChange={(value) => onFieldChange((current) => ({ ...current, gender: value }))}
            options={GENDER_OPTIONS}
            placeholder="Select gender"
          />

          <SelectInput
            label="Marital Status"
            value={employeeForm.maritalStatus}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, maritalStatus: value }))
            }
            options={MARITAL_STATUS_OPTIONS}
            placeholder="Select status"
          />
        </div>
      </section>

      <section className="form-section-card">
        <div className="section-heading">
          <h3>Employment Information</h3>
        </div>

        <div className="employee-form-grid">
          <StaticInput label="Employee ID" required value={employeeIdValue} />

          <SelectInput
            label="Department"
            required
            value={employeeForm.department}
            onChange={(value) =>
              onFieldChange((current) => ({
                ...current,
                department: value,
                designation: current.department === value ? current.designation : "",
              }))
            }
            options={departmentOptions}
            placeholder="Select department"
          />

          <SelectInput
            label="Designation"
            required
            value={employeeForm.designation}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, designation: value }))
            }
            options={designationOptions}
            placeholder="Select designation"
          />

          <SelectInput
            label="Role"
            required
            value={employeeForm.role}
            onChange={(value) => onFieldChange((current) => ({ ...current, role: value }))}
            options={ROLE_OPTIONS}
            placeholder="Select role"
          />

          <DateInput
            label="Date of Joining"
            required
            value={employeeForm.dateOfJoining}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, dateOfJoining: value }))
            }
          />

          <SelectInput
            label="Employment Type"
            required
            value={employeeForm.employmentType}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, employmentType: value }))
            }
            options={EMPLOYMENT_TYPES}
            placeholder="Select type"
          />

          <LabeledInput
            label="Work Shift Start"
            required
            type="time"
            value={employeeForm.workShiftStart}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, workShiftStart: value }))
            }
          />

          <LabeledInput
            label="Work Shift End"
            required
            type="time"
            value={employeeForm.workShiftEnd}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, workShiftEnd: value }))
            }
          />

          <SelectInput
            label="Reporting Manager"
            value={employeeForm.reportingManager}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, reportingManager: value }))
            }
            options={reportingManagerOptions}
            placeholder="Select manager"
          />

          <LabeledInput
            label="Username"
            required
            value={employeeForm.username}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, username: value }))
            }
            placeholder="Enter username"
          />

          {showPasswordFields ? (
            <LabeledInput
              label="Password"
              required
              value={employeeForm.loginPassword}
              onChange={(value) =>
                onFieldChange((current) => ({ ...current, loginPassword: value }))
              }
              placeholder="Create password"
              type="password"
            />
          ) : null}
        </div>
      </section>

      <section className="form-section-card">
        <div className="section-heading">
          <h3>Salary Information</h3>
        </div>

        <div className="employee-form-grid">
          <LabeledInput
            label="Basic Salary (₹)"
            required
            value={employeeForm.basicSalary}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, basicSalary: value }))
            }
            placeholder="Enter basic salary"
            type="number"
          />

          <LabeledInput
            label="House Rent Allowance (₹)"
            value={employeeForm.houseRentAllowance}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, houseRentAllowance: value }))
            }
            placeholder="Enter HRA amount"
            type="number"
          />

          <LabeledInput
            label="Conveyance Allowance (₹)"
            value={employeeForm.conveyanceAllowance}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, conveyanceAllowance: value }))
            }
            placeholder="Enter conveyance amount"
            type="number"
          />

          <LabeledInput
            label="Special Allowance (₹)"
            value={employeeForm.specialAllowance}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, specialAllowance: value }))
            }
            placeholder="Enter special allowance"
            type="number"
          />

          <LabeledInput
            label="Provident Fund (₹)"
            value={employeeForm.providentFund}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, providentFund: value }))
            }
            placeholder="Enter PF deduction"
            type="number"
          />

          <LabeledInput
            label="Professional Tax (₹)"
            value={employeeForm.professionalTax}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, professionalTax: value }))
            }
            placeholder="Enter professional tax"
            type="number"
          />

          <LabeledInput
            label="Income Tax / TDS (₹)"
            value={employeeForm.incomeTax}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, incomeTax: value }))
            }
            placeholder="Enter income tax"
            type="number"
          />

          <SelectInput
            label="Pay Frequency"
            required
            value={employeeForm.payFrequency}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, payFrequency: value }))
            }
            options={PAY_FREQUENCIES}
            placeholder="Select frequency"
          />

          <SelectInput
            label="Tax Regime"
            value={employeeForm.taxRegime}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, taxRegime: value }))
            }
            options={TAX_REGIME_OPTIONS}
            placeholder="Select regime"
          />

          <LabeledInput
            label="Bank Name"
            required
            value={employeeForm.bankName}
            onChange={(value) => onFieldChange((current) => ({ ...current, bankName: value }))}
            placeholder="Enter bank name"
          />

          <LabeledInput
            label="Account Number"
            required
            value={employeeForm.accountNumber}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, accountNumber: value }))
            }
            placeholder="Enter account number"
          />

          <LabeledInput
            label="IFSC Code"
            required
            value={employeeForm.ifscCode}
            onChange={(value) => onFieldChange((current) => ({ ...current, ifscCode: value }))}
            placeholder="Enter IFSC code"
          />

          <LabeledInput
            label="PAN Number"
            value={employeeForm.panNumber}
            onChange={(value) => onFieldChange((current) => ({ ...current, panNumber: value }))}
            placeholder="Enter PAN number"
          />

          <LabeledInput
            label="UAN Number"
            value={employeeForm.uanNumber}
            onChange={(value) => onFieldChange((current) => ({ ...current, uanNumber: value }))}
            placeholder="Enter UAN number"
          />

          <LabeledInput
            label="PF Number"
            value={employeeForm.pfNumber}
            onChange={(value) => onFieldChange((current) => ({ ...current, pfNumber: value }))}
            placeholder="Enter PF number"
          />
        </div>
      </section>

      <section className="form-section-card">
        <div className="section-heading">
          <h3>Address Information</h3>
        </div>

        <div className="employee-form-grid address">
          <LabeledInput
            label="Address Line 1"
            required
            value={employeeForm.addressLine1}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, addressLine1: value }))
            }
            placeholder="Enter address line 1"
          />

          <LabeledInput
            label="Address Line 2"
            value={employeeForm.addressLine2}
            onChange={(value) =>
              onFieldChange((current) => ({ ...current, addressLine2: value }))
            }
            placeholder="Enter address line 2"
          />

          <LabeledInput
            label="City"
            required
            value={employeeForm.city}
            onChange={(value) => onFieldChange((current) => ({ ...current, city: value }))}
            placeholder="Enter city"
          />

          <SelectInput
            label="State"
            required
            value={employeeForm.state}
            onChange={(value) => onFieldChange((current) => ({ ...current, state: value }))}
            options={STATE_OPTIONS}
            placeholder="Select state"
          />

          <LabeledInput
            label="PIN Code"
            required
            value={employeeForm.pinCode}
            onChange={(value) => onFieldChange((current) => ({ ...current, pinCode: value }))}
            placeholder="Enter PIN code"
          />

          <SelectInput
            label="Country"
            required
            value={employeeForm.country}
            onChange={(value) => onFieldChange((current) => ({ ...current, country: value }))}
            options={COUNTRY_OPTIONS}
            placeholder="Select country"
          />
        </div>
      </section>

      <section className="form-section-card">
        <div className="section-heading">
          <h3>Documents (Upload)</h3>
        </div>

        <div className="document-grid">
          {DOCUMENT_FIELDS.map((field) => (
            <DocumentUploadCard
              key={field.key}
              label={field.label}
              file={employeeFiles[field.key]}
              existingFile={existingDocuments[field.key]}
              onSelect={(file) =>
                onFileChange((current) => ({
                  ...current,
                  [field.key]: file,
                }))
              }
            />
          ))}
        </div>
      </section>

      <div className="form-footer">
        <span className="required-note">* Marked fields are required</span>
        <div className="form-footer-actions">
          <button className="secondary-button" type="button" onClick={onCancel}>
            {mode === "edit" ? "Cancel Edit" : "Cancel"}
          </button>
          <button className="primary-button save-button" type="submit">
            {isSavingEmployee ? "Saving..." : submitLabel || (mode === "edit" ? "Save Changes" : "Save Employee")}
          </button>
        </div>
      </div>
    </form>
  );
}
